import SDKMessageBus from '../utility/message-bus';
import cacheCleared from './cacheCleared';
import canUser from './canUser';
import getCurrentLanguageCode from './getCurrentLanguageCode';
import getFeatureConfig from './getFeatureConfig';
import getPlatformInfo from './getPlatformInfo';
import getAuthToken from './getAuthToken';
import { getQRCode } from './qrCode';
import isNavigable from './isNavigable';
import languageChanged from './languageChanged';
import paramsChanged from './paramsChanged';
import pickContact from './pickContact';
import platformScrollChanged from './platformScrollChanged';
import promptForMFA from './promptForMFA';
import refetchRequired from './refetchRequired';
import requestExtensionData from './requestExtensionData';
import requestOutletInfo from './requestOutletInfo';
import requestPlatformDataSetup from './requestPlatformData';
import takePicture from './takePicture';
import { BehaviorSubject } from 'rxjs';
import { IDict } from '../types/utility';
import { ITranslationSetup } from '../setup/t';
import { TectonResponse, ISources } from '../types';
import { getTectonCacheSetup, setTectonCacheSetup } from './tectonCache';
import getPushEnrollmentSetup from './getPushEnrollment';

export default function setupSources(
    messageBus: SDKMessageBus,
    moduleInstanceId: string,
    sourceInfo: ITranslationSetup,
    teardowns: (() => void)[],
    platformCapabilities: Set<string>,
    params$: BehaviorSubject<IDict<string>>
): ISources {
    const requestPlatformData = requestPlatformDataSetup(messageBus, platformCapabilities);
    const fetchPlatformData =
        requestPlatformData &&
        function(route: string): Promise<TectonResponse> {
            return requestPlatformData({ route, method: 'GET' });
        };

    const sources: ISources = {
        cacheCleared: cacheCleared(messageBus, teardowns, platformCapabilities),
        canUser: canUser(messageBus, platformCapabilities),
        fetchPlatformData,
        getCurrentLanguageCode: getCurrentLanguageCode(sourceInfo.$locale),
        getFeatureConfig: getFeatureConfig(messageBus, moduleInstanceId, platformCapabilities),
        getPlatformInfo: getPlatformInfo(messageBus, platformCapabilities),
        getAuthToken: getAuthToken(messageBus, platformCapabilities),
        isNavigable: isNavigable(messageBus, platformCapabilities),
        languageChanged: languageChanged(sourceInfo.$locale, platformCapabilities, teardowns),
        loc: sourceInfo.tFn,
        paramsChanged: paramsChanged(params$, teardowns),
        pickContact: pickContact(messageBus, platformCapabilities),
        getPushEnrollment: getPushEnrollmentSetup(messageBus, platformCapabilities),
        getQRCode: getQRCode(messageBus, platformCapabilities),
        platformScrollChanged: platformScrollChanged(
            messageBus,
            teardowns,
            platformCapabilities,
            moduleInstanceId
        ),
        promptForMFA: promptForMFA(messageBus, moduleInstanceId, platformCapabilities),
        refetchRequired: refetchRequired(messageBus, teardowns, platformCapabilities),
        requestExtensionData: requestExtensionData(
            messageBus,
            platformCapabilities,
            moduleInstanceId
        ),
        requestOutletInfo: requestOutletInfo(messageBus, platformCapabilities),
        requestPlatformData,
        takePicture: takePicture(messageBus, platformCapabilities),
        getTectonCacheObject: getTectonCacheSetup(
            messageBus,
            moduleInstanceId,
            platformCapabilities
        ),
        setTectonCacheObject: setTectonCacheSetup(
            messageBus,
            moduleInstanceId,
            platformCapabilities
        )
    };

    return sources;
}
